/**
 * Composite キャンバス構築（happa v1.1 Phase E Step 4 / Phase F Step 4 /
 * Phase H Step 3）。
 *
 * Surface 色 + optional Surface オーバーレイ（`state.overlays.surface`）を
 * 1 枚の offscreen canvas に合成する「fill 元」を作る pure ファクトリ。
 *
 * 二相モデルでの使われ方:
 * - **pre-explosion**: silhouette path で clip を張り、composite を
 *   `drawImage(composite, 0, 0)` でコピー。結果として silhouette の中に
 *   surface + overlay が収まる。
 * - **explosion**: Unified Path2D（全破片を結合）で clip を張り、
 *   composite を `drawImage(composite, 0, 0)`。破片 1 枚 1 枚の形に
 *   composite がくり抜かれるので、割れる画像表現が得られる。
 *
 * overlay が null（または描画対象なし）なら、本モジュールは使わず
 * 呼び出し側が solid fill にフォールバックする（fast path）。その判定は
 * `renderer.ts` が行い、本モジュールには overlay が **non-null**
 * のときだけ入ってくる想定。ただし防御的に null を受けても no-op で
 * surface 塗りだけした canvas を返すよう実装する。
 *
 * Phase F Step 4 変更:
 * 旧 `overlayImage: HTMLImageElement | null` 引数を、state 層と同じ構造の
 * `overlay: OverlayImage | null`（`{image, dataUrl, filename}`）に差し替え。
 * 本モジュール内部では `overlay?.image` を参照し、それ以外のフィールド
 * （dataUrl / filename）は無視する（将来の永続化 / UI 表示で使う想定）。
 * cache key は従来通り `overlay.image.src` で同一性を判定する。
 *
 * キャッシュ方針:
 * - module-level の single-slot キャッシュを持つ。サイズ・色・overlay 画像
 *   参照・silhouette の bbox が同じなら再利用、そうでなければ作り直し。
 * - silhouette の bbox が毎フレーム変わる（Breath の Pulse / 破片追従 warp）
 *   ため、bbox もキーに含める。Phase H Step 3 で整数丸めを撤廃し、Pulse 小
 *   振幅でも cache が粘着して PNG が silhouette から取り残される症状を解消。
 * - 実運用では「idle 中は Pulse で bbox が常に微変化」「explosion 中は
 *   毎フレーム bbox 変化」の二極で、いずれも毎フレーム再構築で実測 1ms 以内。
 *
 * Phase H Step 3（2026-04-22）:
 * letterbox fit → **cover fit** に変更。PNG は silhouette AABB を完全に覆い、
 * 余剰は source 側でクロップする。Inner / Background overlay と同じ cover
 * セマンティクスで 3 スロットの fit 戦略を揃えた。
 *
 * Phase H Step 1（2026-04-22）: Breath から Crackle / Rotation を削除したため
 * 旧版で導入していた rotation 追従ロジック（`rotationAngle` / `rotationCenter`
 * パラメータと unrotate ヘルパー）は撤去。Pulse は一様スケールで silhouette
 * の AABB がそのまま追従するため、bbox 再計算だけで PNG と silhouette が
 * 1:1 で動く。
 *
 * 設計参照:
 * - docs/happa-designtool-v1.1-spec.md §3.3 / §8（レンダリングパイプライン）
 * - docs/happa-designtool-v1.1-spec.md Phase E Step 4（PNG Overlay）
 * - docs/happa-designtool-v1.1-spec.md Phase F Step 4（3 スロット overlay）
 * - docs/happa-designtool-v1.1-spec.md Phase H Step 3（Surface PNG が
 *   silhouette と追従する cover fit）
 */

import { polygonAABB } from '../core/polygon';
import type { Vec2 } from '../core/types';
import type { OverlayImage } from '../state/types';
import { computeCoverRect } from './layout';

// ── module-level キャッシュ ──────────────────────────────────────────
// 単一 canvas 前提（renderer.ts と同じ運用）。複数キャンバスを並行描画する
// 要件が出たら、クロージャ化 or per-canvas マップへ移行する。

/**
 * キャッシュ済み composite canvas。未構築 or invalidate 後は null。
 * HTMLCanvasElement は `document.createElement('canvas')` で作る（OffscreenCanvas
 * は Safari 互換性が不安定なため採用しない）。
 */
let cachedComposite: HTMLCanvasElement | null = null;

/**
 * キャッシュキー（文字列化）。outer サイズ / surface color / overlay src /
 * silhouette の bbox を連結する。Phase H Step 3 で bbox の丸めを撤廃し、
 * sub-px 変動もキーの差として検出することで PNG の silhouette 追従を確実化。
 */
let cachedKey: string | null = null;

/**
 * Composite 入力パラメータ。
 *
 * - `overlay`: 非 null のときだけ本モジュールを呼ぶのが呼び出し側規約
 *   だが、念のため null でも動作する（surface 一色塗りの canvas を返す）。
 *   Phase F Step 4 で `HTMLImageElement` 直接受け取りを `OverlayImage`
 *   ラッパー（`{image, dataUrl, filename}`）に変更。本モジュール内部では
 *   `overlay.image` のみを参照する。
 * - `silhouettePolygon`: bbox 計算用。Pulse で毎フレーム微変化する想定で、
 *   cache key に 4 値（minX/minY/maxX/maxY）を畳み込むため丸めない。
 * - `outerW` / `outerH`: composite canvas の物理サイズ。main canvas と同寸。
 */
export interface CompositeParams {
  outerW: number;
  outerH: number;
  surfaceColor: string;
  overlay: OverlayImage | null;
  silhouettePolygon: Vec2[];
}

/**
 * Surface 色 + overlay 画像を合成した canvas を返す（Phase E Step 4 /
 * Phase F Step 4 / Phase H Step 3）。
 *
 * 処理:
 * 1. キャッシュキーが一致すれば既存 canvas を再利用
 * 2. outer サイズで `<canvas>` を確保（必要なら resize）
 * 3. 全面を `surfaceColor` で fill
 * 4. `overlay.image` が非 null かつ画像サイズ > 0 なら、silhouette の AABB に
 *    **cover fit** で `drawImage` する（Phase H Step 3）。
 *
 * 本関数は純粋に offscreen canvas を返すだけで、呼び出し側の ctx には
 * 一切触れない。呼び出し側が `ctx.clip()` → `drawImage(composite, 0, 0)` の
 * 形で使う。
 *
 * `overlay.image.complete === false`（まだロード中）のケースは drawImage が
 * 例外を投げるリスクがあるため、本関数内で `complete && naturalWidth > 0`
 * チェックをかけ、未完了なら overlay を描かず surface 一色だけの composite
 * を返す（ロード完了後に next frame で自然に反映される）。
 *
 * @param params composite 構築パラメータ（outer サイズ / surface 色 /
 *               Surface overlay / silhouette polygon）。
 * @returns surface + Surface overlay を合成済みの `HTMLCanvasElement`（outer 寸法）。
 */
export function buildComposite(params: CompositeParams): HTMLCanvasElement {
  const { outerW, outerH, surfaceColor, overlay, silhouettePolygon } = params;
  // overlay ラッパーから画像参照を取り出す。以降のロジックは Phase E 時代と
  // 同じ HTMLImageElement ベースで動く（dataUrl / filename は本関数では不要）。
  const overlayImage = overlay?.image ?? null;

  // silhouette が退化している（頂点 < 3）ケースでは AABB は無限大を
  // 返すので cache key 的には「bbox なし」として "none" を入れ、overlay 描画
  // も skip する。Phase H Step 3 で整数丸めを撤廃（Pulse 小振幅で cache が
  // 粘着していた問題の対策）。
  const hasValidSilhouette = silhouettePolygon.length >= 3;
  const aabb = hasValidSilhouette ? polygonAABB(silhouettePolygon) : null;
  const bboxKey = aabb === null ? 'none' : `${aabb[0]},${aabb[1]},${aabb[2]},${aabb[3]}`;
  // overlay 参照が変わったかは src で見る（同一画像の再ロード = src 同じ = cache hit）。
  // null / 未ロードのケースは 'none' キーにまとめる。
  const overlayKey =
    overlayImage?.complete && overlayImage.naturalWidth > 0 ? overlayImage.src : 'none';
  const key = `${outerW}x${outerH}::${surfaceColor}::${overlayKey}::${bboxKey}`;

  if (cachedComposite !== null && cachedKey === key) {
    return cachedComposite;
  }

  // canvas の確保 or 再利用。サイズが違えば width/height を書き換える
  // （これで内容もリセットされる仕様、以降の fill/drawImage でまっさらに塗る）。
  const canvas = cachedComposite ?? document.createElement('canvas');
  if (canvas.width !== outerW) canvas.width = outerW;
  if (canvas.height !== outerH) canvas.height = outerH;

  const cctx = canvas.getContext('2d');
  if (cctx === null) {
    // getContext が null を返すのは極めて稀（メモリ不足等）。フォールバックで
    // cache 無効化しつつ新規 canvas をそのまま返す（呼び出し側の drawImage は
    // 空 canvas をコピーするだけで済む）。
    cachedComposite = null;
    cachedKey = null;
    return canvas;
  }

  // 1. 全面 surface color。cover fit の PNG が silhouette AABB を覆い切る
  //    ため、silhouette clip 内側では PNG が見え、clip 外は呼び出し側で
  //    落とされる。
  cctx.fillStyle = surfaceColor;
  cctx.fillRect(0, 0, outerW, outerH);

  // 2. overlay 描画（ロード完了 + 有効な AABB のときだけ）。null / 未ロード /
  //    bbox 退化は no-op。
  if (aabb !== null) {
    drawOverlayCover(cctx, overlayImage, aabb);
  }

  cachedComposite = canvas;
  cachedKey = key;
  return canvas;
}

/**
 * 合成キャンバスの context に overlay を cover fit で描画する
 * （Phase H Step 3）。
 *
 * 描画条件:
 * - `img` がロード完了しており `naturalWidth/Height > 0`
 * - AABB が正の幅/高を持つ
 *
 * いずれか満たさない場合は no-op で、呼び出し側の `fillRect(surface)` だけが
 * composite に残る。
 */
function drawOverlayCover(
  cctx: CanvasRenderingContext2D,
  img: HTMLImageElement | null,
  aabb: [number, number, number, number],
): void {
  if (img === null) return;
  if (!img.complete) return;
  if (img.naturalWidth <= 0 || img.naturalHeight <= 0) return;
  const [minX, minY, maxX, maxY] = aabb;
  const bboxW = maxX - minX;
  const bboxH = maxY - minY;
  if (bboxW <= 0 || bboxH <= 0) return;

  // Phase H Step 3: letterbox → cover。PNG は silhouette AABB を完全に
  // 覆うサイズで配置し、余剰は source 側でクロップする。silhouette clip は
  // 呼び出し側（renderer）が張るため、AABB の角が余る心配はない（silhouette
  // 内側では必ず PNG が見える）。
  const { sx, sy, sw, sh } = computeCoverRect(img.naturalWidth, img.naturalHeight, bboxW, bboxH);
  cctx.imageSmoothingEnabled = true;
  cctx.imageSmoothingQuality = 'high';
  cctx.drawImage(img, sx, sy, sw, sh, minX, minY, bboxW, bboxH);
}

/**
 * テスト / リソース整理用に composite キャッシュを破棄する。
 *
 * 本番コードパスから通常呼ばれることはない（キャッシュキー不一致で自動的に
 * 再構築される）。明示的なメモリ解放が必要な場面（page unload 等）や
 * テストの各ケース独立性を守りたい場面で使う想定。
 */
export function resetCompositeCache(): void {
  cachedComposite = null;
  cachedKey = null;
}

/**
 * DETONATE 遷移時に使う「凍結マスター composite」を構築する（Phase E Step 4
 * バグ修正、2026-04-21）。
 *
 * 通常の `buildComposite` は module-level single-slot キャッシュを使うため、
 * idle 中の breath で silhouette が変わるたびに内容がミューテートされ、
 * 参照を保持しても内容は保証されない。DETONATE 中は「DETONATE の瞬間の
 * silhouette で焼いた master 画像」を破片数分コピーする必要があるため、
 * キャッシュから**独立した新規 canvas** に合成結果をコピーして返す。
 *
 * 破片描画側は本関数の戻り値を per-cell transform + clip(cell.points) +
 * drawImage(master, 0, 0) の形で使う。詳細は `explosion.ts` を参照。
 *
 * @param params composite 構築パラメータ。`buildComposite` と同型。
 * @returns DETONATE 開始時の surface + overlay を焼き込んだ独立 canvas。
 */
export function buildMasterComposite(params: CompositeParams): HTMLCanvasElement {
  const src = buildComposite(params);
  const dst = document.createElement('canvas');
  dst.width = src.width;
  dst.height = src.height;
  const dctx = dst.getContext('2d');
  if (dctx !== null) dctx.drawImage(src, 0, 0);
  return dst;
}
